import { Alert, Box, CloseButton } from '@chakra-ui/react'
import { useGetAlertBannersQuery } from 'content-service'
import { AlertBannerIcon } from 'icons'
import { useEffect, useMemo, useState } from 'react'
import { RichTextRenderer } from 'ui'
import { bannerColorToBgAndBorder } from 'utils'

interface AlertBannerProps {
  preview?: boolean
  locale: string
  id?: string
}

export default function AlertBanners({ preview, locale, id = '' }: AlertBannerProps) {
  const { data, loading } = useGetAlertBannersQuery({
    variables: { preview, locale, id },
  })

  const globalBanner = data?.globalBanner?.items?.[0] ?? null
  const pageBanner = data?.pageBanner ?? null

  // If page banner suppresses global banner, remove global banner from list
  const visibleBanners = useMemo(() => {
    return [
      ...(globalBanner && !pageBanner?.suppressGlobalAlertBanner ? [globalBanner] : []),
      ...(pageBanner ? [pageBanner] : []),
    ]
  }, [globalBanner, pageBanner])

  const [dismissedIds, setDismissedIds] = useState<string[]>([])
  const [dismissedReady, setDismissedReady] = useState(false)

  const SESSION_KEY = 'dismissedAlertBannerIds'

  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setDismissedIds(parsed)
      } catch {
        sessionStorage.removeItem(SESSION_KEY)
      }
    }

    setDismissedReady(true)
  }, [])

  const handleDismiss = (id: string) => {
    const updated = [...dismissedIds, id]
    setDismissedIds(updated)
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(updated))
  }

  const filteredBanners = useMemo(() => {
    if (!dismissedReady || loading) return []
    return visibleBanners.filter((banner) => !dismissedIds.includes(banner.sys.id))
  }, [dismissedReady, loading, visibleBanners, dismissedIds])

  return (
    <Box>
      {filteredBanners.map((banner) => {
        const { bg, border } = bannerColorToBgAndBorder(banner.backgroundColour ?? 'manapou-orange')
        const { description, displayIcon } = banner

        return (
          <Alert
            key={banner.sys.id}
            className="alert-banner"
            bg={bg}
            pt={4}
            pb={4}
            pr={12}
            pl={{ base: 6 }}
            rounded="md"
            borderBottomWidth="4px"
            borderBottomColor={border}
            display="flex"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            status="warning"
            position="relative"
          >
            <Box display="flex" alignItems="center" gap={3} flex="1" justifyContent="center">
              {displayIcon && (
                <Box
                  bg="orange.500"
                  borderRadius="full"
                  boxSize={9}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  pb={1}
                >
                  <AlertBannerIcon />
                </Box>
              )}
              <Box textAlign="left" minW={0} fontSize={{ base: 'xs', md: 'sm' }}>
                {description && <RichTextRenderer richText={description} />}
              </Box>
            </Box>
            <CloseButton
              position="absolute"
              top="1"
              right="1"
              onClick={() => handleDismiss(banner.sys.id)}
            />
          </Alert>
        )
      })}
    </Box>
  )
}
